
import { supabase } from '@/lib/supabase/client';
import {
  CreateDealerReportData,
  DealerReportWithDetails,
  ReportStatus,
  ReportType,
} from '@/types/dealerReport';
import { sendDealerReportNotificationEmail, type DealerReportData } from '@/services/email/emailService';

/**
 * Creates a new dealer report.
 * Corresponds to RPC function: user_create_dealer_report
 * @param reportData - The data for the new report.
 * @returns The ID of the newly created report.
 */
export async function createDealerReport(reportData: CreateDealerReportData): Promise<string> {
  const { data, error } = await supabase.rpc('user_create_dealer_report', {
    p_dealer_id: reportData.dealer_id,
    p_report_type: reportData.report_type,
    p_description: reportData.description,
  });

  if (error) {
    console.error('Error creating dealer report:', error.message);
    if (error.message.includes('AUTH_REQUIRED')) {
      throw new Error("Devi effettuare l'accesso per poter inviare una segnalazione.");
    }
    if (error.message.includes('DUPLICATE_REPORT')) {
      throw new Error('Hai già segnalato questo dealer per lo stesso motivo.');
    }
    throw new Error("Si è verificato un errore durante l'invio della segnalazione.");
  }

  // Dopo aver creato la segnalazione, invia l'email di notifica
  try {
    console.log('🔄 [DEALER REPORT RPC] Tentativo invio email di notifica per report ID:', data);
    
    // Ottieni i dettagli del dealer per l'email
    const { data: dealerDetails, error: dealerDetailsError } = await supabase
      .from('dealers')
      .select('name, address, city')
      .eq('id', reportData.dealer_id)
      .single();

    // Ottieni i dettagli dell'utente per l'email
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.warn('⚠️  [DEALER REPORT RPC] Utente non autenticato per email');
      return data;
    }

    const { data: userProfile, error: userProfileError } = await supabase
      .from('user_profiles')
      .select('first_name, last_name')
      .eq('user_id', user.id)
      .single();

    if (dealerDetails) {
      const emailData: DealerReportData = {
        id: data,
        dealerName: dealerDetails.name,
        dealerAddress: dealerDetails.address,
        dealerCity: dealerDetails.city,
        reportType: reportData.report_type,
        description: reportData.description || null,
        userEmail: user.email || null,
        userName: userProfile ? `${userProfile.first_name} ${userProfile.last_name}`.trim() : null,
        created_at: new Date().toISOString()
      };

      console.log('📤 [DEALER REPORT RPC] Chiamata sendDealerReportNotificationEmail...');
      const emailResult = await sendDealerReportNotificationEmail(emailData);
      
      if (emailResult.success) {
        console.log('✅ [DEALER REPORT RPC] Email inviata con successo');
      } else {
        console.error('❌ [DEALER REPORT RPC] Errore invio email:', emailResult.error);
        // Non propagare l'errore email per non bloccare la segnalazione
      }
    } else {
      console.warn('⚠️  [DEALER REPORT RPC] Nessun dettaglio dealer trovato, email non inviata');
    }
  } catch (emailError) {
    console.error('💥 [DEALER REPORT RPC] Eccezione durante invio email:', emailError);
    // Non propagare l'errore email per non bloccare la segnalazione
  }

  return data;
}

/**
 * Fetches reports for the currently authenticated user.
 * Corresponds to RPC function: user_get_dealer_reports
 * @returns A list of the user's reports.
 */
export async function getUserDealerReports(): Promise<any[]> {
  const { data, error } = await supabase.rpc('user_get_dealer_reports');
  if (error) {
    console.error('Error fetching user dealer reports:', error);
    throw new Error('Impossibile recuperare le tue segnalazioni.');
  }
  return data || [];
}

/**
 * Fetches all dealer reports (admin only).
 * Corresponds to RPC function: admin_get_dealer_reports
 * @param status - Optional filter by report status.
 * @param limit - The maximum number of reports to return.
 * @param offset - The number of reports to skip (for pagination).
 * @returns A list of dealer reports with details.
 */
export async function getDealerReports(
  status?: ReportStatus,
  limit: number = 50,
  offset: number = 0
): Promise<DealerReportWithDetails[]> {
  const { data, error } = await supabase.rpc('admin_get_dealer_reports', {
    p_status: status,
    p_limit: limit,
    p_offset: offset,
  });
  if (error) {
    console.error('Error fetching dealer reports:', error);
    throw new Error('Impossibile recuperare le segnalazioni.');
  }
  return data || [];
}

/**
 * Updates the status of a dealer report (admin only).
 * Corresponds to RPC function: admin_update_dealer_report_status
 * @param reportId - The ID of the report to update.
 * @param status - The new status for the report.
 * @param adminNotes - Optional notes from the admin.
 * @returns True if the update was successful.
 */
export async function updateDealerReportStatus(
  reportId: string,
  status: ReportStatus,
  adminNotes?: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('admin_update_dealer_report_status', {
    p_report_id: reportId,
    p_status: status,
    p_admin_notes: adminNotes,
  });

  if (error) {
    console.error('Error updating dealer report status:', error);
    throw new Error("Si è verificato un errore durante l'aggiornamento della segnalazione.");
  }
  return data;
}
